/**
 * @file machinelogic_app_builder.js
 * @author MachineApps Team
 *
 * @description Offers a programmatic way to make a MachineLogic Application
 * that does NOT go through machinelogic.js. This is especially useful for
 * the MachineApps team.
 *
 * The intention will be for the user to create their BuildableApp and, once
 * created, turn it into  a MachineLogic App using the 'generate' function.
 * It is helpful to keep the app as a BuildableApp while creating it so that
 * we have full control over its contents before transforming it to a MachineLogic
 * application.
 *
 */

"use strict";
const { Instruction } = require('../machinelogic/Instruction');
const { ApplicationUI } = require('../machinelogic/ApplicationUI');
const { widgetTypes, uiWidgetDimensions } = require('../machinelogic/constants');
const { v4: uuidv4 } = require('uuid');
const { ApplicationTypes } = require('../machinelogic/constants/ApplicationTypes');
const validateInstructionParameter = Instruction.validateInstructionParameter;
const ApplicationFactory = require('../machinelogic/factories/ApplicationFactory');

function MachineLogicAppBuilder(pLibrary) {
  this.mLibrary           = pLibrary;
  this.instructions       = {};
  this.widgets            = {};
  this.pseudoInstructions = {};
  this.mRobotClipOptions = {
    speedScalingFactor: 0.5,
    accelerationScalingFactor: 0.5,
    payloadWeight: undefined,
    referenceFrameWorld: identity4x4MatrixArray,
  };

  // -- Generate all possible actions from the instruction set.
  const lInstructionSet = this.mLibrary.getInstructionSet();
  const lThis = this;
  Object.keys(lInstructionSet).forEach(function(pCategoryName) {
    const lCategorySet = lInstructionSet[pCategoryName];
    lThis.instructions[pCategoryName] = {};

    Object.keys(lCategorySet).forEach(function(pInstructionName) {
      const lInstruction = lCategorySet[pInstructionName];
      const lParameters = lInstruction.parameters;

      lThis.instructions[pCategoryName][pInstructionName] = function(pData) {
        pData = pData || {};
        if (typeof pData !== 'object') {
          console.error('Data for an instruction must be an object. Received: ', pData);
          return undefined;
        }

        const lIsInvalidData = Object.keys(pData).some(function(pParameterName) {
          const lParamInfo = lParameters.find(function(pParam) { return pParam.name === pParameterName; });
          const lErrors = validateInstructionParameter(lParameters, lParamInfo, pParameterName, pData[pParameterName], lThis.mLibrary.getConfigManager());
          if (Object.keys(lErrors).length) {
            console.error(`Invalid format in data=${pParameterName}, category=${pCategoryName}, instruction=${pInstructionName}.`);
            return true;
          }

          return false;
        });

        if (lIsInvalidData) {
          console.error('Invalid data. Cannot create the instruction. Valid instruction is of shape: ', lParameters);
          return undefined;
        }

        if (pCategoryName === 'log') {
          // Note: For log  messages, we need to wrap the string in ` for it to be valid.
          pData.message = "`" + pData.message + "`";
        }

        return new BuildableInstruction(pCategoryName, pInstructionName, pData);
      };
    });
  });

  // -- Generate all possible widgets.
  widgetTypes.forEach(function(pWidgetType) {
    lThis.widgets[pWidgetType] = function(pData) {
      return new BuildableWidget(pWidgetType, pData || {});
    };
  });

  // These pseudo ML statement also handles the sequence that is executed by the conditional
  // It behaves more like a ML sequence than a ML condition instruction
  this.pseudoInstructions.if = function(pExpression, pSequence = undefined, pInstructionType = "if", pExecute = "inSeries") {
    return new MachineLogicPseudoIfStatement(pExpression, pInstructionType, pExecute, pSequence, lThis);
  };
  this.pseudoInstructions.while = function(pExpression, pSequence = undefined) {
    return new MachineLogicPseudoWhileStatement(pExpression, pSequence, lThis);
  };
}

// Function that takes any expressions, evaluates it and stores it in a variable.
// This is the only way to pass an arbitrary expression to a if/while loop in ML
function generateVarFromComplexExpression(pExpression, pAppBuilder, pBuildableApp, pParentSequence){
  // The variable with with we will see the complex expression
  // (jnthjackson) TODO we could have some variable/logic checking in here
  const expressionVariableName = ("exp_var_" + uuidv4()).toUpperCase().replace(/-/g, '_');
  pBuildableApp.addVariable(expressionVariableName, "const", { default_value: 0 });
  pParentSequence.addInstruction(pAppBuilder.instructions.setVariable.
    fromExpression({ variableName: expressionVariableName, expression: pExpression}));
  return expressionVariableName;
}

MachineLogicAppBuilder.prototype.createApp = function(pName) {
  const lApp = new BuildableApp(this.mLibrary, pName);
  return lApp;
};

MachineLogicAppBuilder.prototype.createSequence = function(pName, pSequenceParams) {
  const lSequence = new BuildableSequence(pName, pSequenceParams);
  return lSequence;
};

/**
 * Sets the payloadWeight that will be used in the default robotClipOptions for all subsequent robot clips
 * @param {number} pPayloadWeight The payload weight as a positive number in kg
 */
MachineLogicAppBuilder.prototype.setRobotPayloadWeight = function(pPayloadWeight) {
  this.mRobotClipOptions.payloadWeight = pPayloadWeight;
};

/**
 * Sets the speedScalingFactor that will be used in the default robotClipOptions for all subsequent robot clips
 * @param {number} pSpeedScalingFactor The speed scaling factor, within [0, 1]
 */
MachineLogicAppBuilder.prototype.setRobotSpeed = function(pSpeedScalingFactor) {
  this.mRobotClipOptions.speedScalingFactor = pSpeedScalingFactor;
};

/**
 * Sets the accelerationScalingFactor that will be used in the default robotClipOptions for all subsequent robot clips
 * @param pAccelerationScalingFactor The acceleration scaling factor, within [0, 1]
 */
MachineLogicAppBuilder.prototype.setRobotAcceleration = function(pAccelerationScalingFactor) {
  this.mRobotClipOptions.accelerationScalingFactor = pAccelerationScalingFactor;
};

/**
 * Sets the referenceFrameWorld that will be used in the default robotClipOptions for all subsequent robot clips
 * @param {number[16]} pReferenceFrameWorld A 4x4 transformation matrix in array form
 */
MachineLogicAppBuilder.prototype.setRobotReferenceFrame = function(pReferenceFrameWorld) {
  this.mRobotClipOptions.referenceFrameWorld = pReferenceFrameWorld;
};

/**
 * Builds a robot clip using the default robotClipOptions set previously.
 * You can override any of these default options by providing yours.
 * @param {string} pName - Name of the clip
 * @param {string} pRobotId - Id of the robot
 * @param {{[referenceFrameWorld]: number[16], [speedScalingFactor]: number, [accelerationScalingFactor]: number, payloadWeight: number}} [pOptions] - Options
 * @return {BuildableRobotClip}
 */
MachineLogicAppBuilder.prototype.createRobotClip = function(pName, pRobotId, pOptions = {}) {
  return new BuildableRobotClip(pName, pRobotId, {
    ...this.mRobotClipOptions,
    ...pOptions
  });
};

MachineLogicAppBuilder.prototype.createKeyframe = function(pJointAngles, pPose) {
  const lKeyframe = new BuildableKeyframe(pJointAngles, pPose);
  return lKeyframe;
};

MachineLogicAppBuilder.prototype.createUi = function() {
  return new BuildableUi();
};

MachineLogicAppBuilder.prototype.createTab = function(pName) {
  return new BuildableUiTab(pName);
};

// -- Defining pseudo instructions that wrap machine logic instructions
// -- IF --
function MachineLogicPseudoIfStatement(pExpression, pInstructionType, pExecute, pSequence, pAppBuilder) {
  this.mAppBuilder = pAppBuilder;
  this.mExpression = pExpression;
  this.mInstructionType = pInstructionType;
  this.mExecute = pExecute;
  this.newSequence = undefined;
  if(pSequence == undefined) {
    this.mSequence = new BuildableSequence("IF_" + uuidv4());
    this.newSequence = true;
  }
  else{
    this.mSequence = pSequence;
    this.newSequence = false;
  }
}
MachineLogicPseudoIfStatement.prototype.getName = function(){return this.mSequence.getName();};
MachineLogicPseudoIfStatement.prototype.addInstruction = function(){this.mSequence.addInstruction();};
MachineLogicPseudoIfStatement.prototype.listInstructions = function(){this.mSequence.listInstructions();};
MachineLogicPseudoIfStatement.prototype.generate = function(pBuildableApp, pParentSequence){
  if(this.newSequence) pBuildableApp.addSequence(this.mSequence);
  const expressionVariable = generateVarFromComplexExpression(this.mExpression, this.mAppBuilder, pBuildableApp, pParentSequence);
  pParentSequence.addInstruction(this.mAppBuilder.instructions.condition.if({
    conditions:
      [
        {
          instructionType: this.mInstructionType,
          conditionExpression: expressionVariable + " == 1",
          execute: this.mExecute,
          sequenceName: this.mSequence.getName()
        }
      ]
  }));
};

// -- WHILE --
// -- Defining pseudo instructions that wrap machine logic instructions
function MachineLogicPseudoWhileStatement(pExpression, pSequence, pAppBuilder) {
  this.mAppBuilder = pAppBuilder;
  this.mExpression = pExpression;
  this.mExecute = "inSeries";
  this.newSequence = undefined;
  if(pSequence == undefined) {
    this.mSequence = new BuildableSequence("WHILE_" + uuidv4());
    this.newSequence = true;
  }
  else{
    this.mSequence = pSequence;
    this.newSequence = false;
  }
}

MachineLogicPseudoWhileStatement.prototype.getName = function(){return this.mSequence.getName();};
MachineLogicPseudoWhileStatement.prototype.addInstruction = function(pInstruction){this.mSequence.addInstruction(pInstruction);};
MachineLogicPseudoWhileStatement.prototype.listInstructions = function(){this.mSequence.listInstructions();};
MachineLogicPseudoWhileStatement.prototype.generate = function(pBuildableApp, pParentSequence){
  if(this.newSequence) pBuildableApp.addSequence(this.mSequence);
  const expressionVariable = generateVarFromComplexExpression(this.mExpression, this.mAppBuilder, pBuildableApp, pParentSequence);
  pParentSequence.addInstruction(this.mAppBuilder.instructions.loop.while({
    conditionExpression: expressionVariable  + " == 1",
    execute: this.mExecute,
    sequenceName: this.mSequence.getName()
  }));
};

function BuildableApp(pLibrary, pName) {
  /** @type {import('../machinelogic').Library} */
  this.mLibrary         = pLibrary;
  this.mName            = pName;
  this.mMainSequence    = null;
  this.mSequenceList    = [];
  this.mClipList        = [];
  this.mVariableList    = [];
  this.mUi              = null;
}

BuildableApp.prototype.reset = function() {
  this.mMainSequence    = null;
  this.mSequenceList    = [];
  this.mClipList        = [];
  this.mVariableList    = [];
  this.mUi              = null;
};

BuildableApp.prototype.addSequence = function(...pSequences) {
  pSequences.forEach(pSequence => this.mSequenceList.push(pSequence));

  return this;
};

BuildableApp.prototype.addRobotClip = function(...pRobotClips) {
  pRobotClips.forEach(pRobotClip => this.mClipList.push(pRobotClip));

  return this;
};

BuildableApp.prototype.setMainSequence = function (pSequence) {
  if (!this.mSequenceList.includes(pSequence)) {
    console.error('Cannot set an unadded sequence as the main sequence.', pSequence);
    return this;
  }

  this.mMainSequence = pSequence;
  return this;
};

/**
   *
   * @returns {BuildableSequence}
   */
BuildableApp.prototype.getMainSequence = function() { return this.mMainSequence; };

/**
   *
   * @returns {Array<BuildableSequence>}
   */
BuildableApp.prototype.listSequences = function() { return this.mSequenceList; };

/**
   *
   * @returns {Array<BuildableRobotClip>}
   */
BuildableApp.prototype.listRobotClips = function() { return this.mClipList; };

BuildableApp.prototype.addVariable = function(pName, pType, pData) {
  this.mVariableList.push({ name: pName, type: pType, data: pData });

  return this;
};
BuildableApp.prototype.listVariables = function() { return this.mVariableList; };

BuildableApp.prototype.setUi = function(pUi) { this.mUi = pUi; return this; };

/**
   *
   * @returns {BuildableUi}
   */
BuildableApp.prototype.getUi = function() { return this.mUi; };

/**
 * @param {ApplicationTypes} type
 * @returns {import('../machinelogic/Application').Application | undefined} The created application, or nothing if an error happened.
 */
BuildableApp.prototype.generate = function(type = ApplicationTypes.MachineLogic) {
  if (type === ApplicationTypes.Python) {
    console.error('We do NOT support generating Python applications');
    return undefined;
  }

  try {
    const lOutApp = ApplicationFactory.create(this.mLibrary, type);
    lOutApp.mName = this.mName;

    // TODO: What a mess! We need to select the application to do interesting things with it. That is unfortunate.
    this.mLibrary.selectApplication(lOutApp);
    const lMainSequenceName = this.getMainSequence().getName();
    const lInitialSequence = lOutApp.getInitialSequence();

    // -- Update the initial sequences name
    if (lMainSequenceName !== lInitialSequence.getName()) {
      lInitialSequence.setName(lMainSequenceName);
    }

    this.listVariables().forEach(function(pVariable) {
      const lParam = lOutApp.addApplicationParameter(pVariable.type);

      lOutApp.updateApplicationParameter(lParam.id, {
        id: lParam.id,
        name: pVariable.name,
        default_value: pVariable.data.default_value,
        inputUuid: pVariable.data.inputUuid
      });
    });

    this.listSequences().forEach(function(pSequence) {
      pSequence.generate(lOutApp, lInitialSequence);
    });

    // Append all the clips to the Application
    if (!lOutApp.robotParameters) {
      lOutApp.robotParameters = { clips: [] };
    }

    lOutApp.robotParameters.clips = [];
    this.listRobotClips().forEach(function(pClip) {
      lOutApp.robotParameters.clips.push(pClip.toJson());
    });

    lOutApp.setInitialSequence(lMainSequenceName);

    if (this.mUi) lOutApp.mUi = this.getUi().generate();

    return lOutApp;
  }
  catch (e) {
    console.error('Unable to create application: ', e);
    return undefined;
  }
};

const identity4x4MatrixArray = [
  1,0,0,0,
  0,1,0,0,
  0,0,1,0,
  0,0,0,1
];

/**
   * Represents a robot clip, which consists of a sequence of keyframes.
   *
   * Keyframes are added to a clip to form a robot path.
   *
   * The clip contains a speed profile which is applied to the keyframes as they are added to the clip.
   *
   * See @ventionco/robot-components/src/components/RobotClipsEditor/utils.js for definition of robot clips.
   * @param {string} pName - Name of the clip
   * @param {string} pRobotId - Id of the robot
   * @param {{[referenceFrameWorld]: number[16], [speedScalingFactor]: number, [accelerationScalingFactor]: number, payloadWeight: number}} [pOptions] - Options
   * @return {BuildableRobotClip} this
   * @constructor
   */
function BuildableRobotClip(pName, pRobotId, { referenceFrameWorld = identity4x4MatrixArray, speedScalingFactor = undefined, accelerationScalingFactor = undefined, payloadWeight = undefined } = {}) {
  this.mName                   = pName;
  this.mRobotId                = pRobotId;   // The EE expects a number even if this is a VID (and that the instructions takes a string VID)
  this.mId                     = uuidv4();
  this.mKeyframes              = [];
  this.mTeachingReferenceFrame = {          // We might need this to me parametrized/get/set-able in the future
    "arrayMatrix__referenceFrame_world": referenceFrameWorld,
  };
  this.mSpeedScalingFactor = speedScalingFactor;
  this.mAccelerationScalingFactor = accelerationScalingFactor;
  this.mPayloadWeight = payloadWeight;
}

BuildableRobotClip.prototype.getId = function() {
  return this.mId;
};

BuildableRobotClip.prototype.toJson = function() {
  return {
    name                      : this.mName,
    robotId                   : this.mRobotId,
    id                        : this.mId,
    keyframes                 : this.mKeyframes,
    teachingReferenceFrame    : this.mTeachingReferenceFrame,
    speedScalingFactor        : this.mSpeedScalingFactor,
    accelerationScalingFactor : this.mAccelerationScalingFactor,
  };
};

/**
   * Add keyframes to the robot clip, applying the speed profile and payload weight if not set.
   * @param {BuildableKeyframe[]} pKeyframes - The keyframes to add
   * @return {BuildableRobotClip} this
   */
BuildableRobotClip.prototype.addKeyframes = function(pKeyframes) {
  // Maybe do some data validation in here
  pKeyframes.forEach(pKeyframe => {
    const lKeyframeJson = pKeyframe.toJson();

    if (!lKeyframeJson.waypoint.velocityScalingFactor) {
      lKeyframeJson.waypoint.velocityScalingFactor = this.mSpeedScalingFactor;
    }

    if (!lKeyframeJson.waypoint.accelerationScalingFactor) {
      lKeyframeJson.waypoint.accelerationScalingFactor = this.mAccelerationScalingFactor;
    }

    if (!lKeyframeJson.setPayload) {
      lKeyframeJson.setPayload = { weight: this.mPayloadWeight };
    }

    this.mKeyframes.push(lKeyframeJson);
  });

  return this;
};

function ImmutableRobotClip(pJson) {
  this.mJson = pJson;
}

ImmutableRobotClip.prototype.toJson = function() {
  return this.mJson;
};

/**
 * Represents a keyframe, which consists of a waypoint.
 *
 * The waypoint has the robot pose we want to reach, as well as a speed profile to use for the motion.
 * @param {number[]} pJointAngles - The joint angles of the robot arm.
 * @param {number[16]} pPose - A 3D transformation matrix in column major array form.
 * @param {{[speedScalingFactor]: number, [accelerationScalingFactor]: number, [payloadWeight]: number}} [pOptions] - Options
 * @return {BuildableKeyframe}
 * @constructor
 */
function BuildableKeyframe(pJointAngles, pPose, { speedScalingFactor = undefined, accelerationScalingFactor = undefined, payloadWeight = -1 } = {}) {
  this.mId = uuidv4();
  this.mTimeStamp = -1;
  this.mIsEditing = false;
  this.mSetPayload = payloadWeight ? { weight : payloadWeight } : null;
  this.mWaypoint = {
    pathType: "movel",
    ignoreReferenceFrame: false,
    blendRadius: -1,
    velocityScalingFactor: speedScalingFactor,
    accelerationScalingFactor: accelerationScalingFactor,
    reteachName: "",
    jointAngles: pJointAngles,
    pose: pPose
  };

  return this;
}

BuildableKeyframe.prototype.toJson = function() {
  return {
    id          : this.mId,
    timeStamp   : this.mTimeStamp,
    isEditing   : this.mIsEditing,
    setPayload  : this.mSetPayload,
    waypoint    : { ...this.mWaypoint }, // Shallow copy, good enough while we're not mutating lists or objects in there
  };
};

function BuildableSequence(pName, pSequenceParams) {
  this.mName              = pName;
  this.mInstructionList   = [];
  this.mClipList          = [];
  if (pSequenceParams) {
    this.mIsLocked = pSequenceParams.isLocked;
  }
}

BuildableSequence.prototype.addInstruction = function() {
  for (let argIdx = 0; argIdx < arguments.length; argIdx++) {
    this.mInstructionList.push(arguments[argIdx]);
  }

  return this;
};

BuildableSequence.prototype.addRobotClip = function(...pRobotClips) {
  pRobotClips.forEach(pRobotClip => this.mClipList.push(pRobotClip));

  return this;
};

/**
 *
 * @returns {Array<BuildableInstruction>}
 */
BuildableSequence.prototype.listInstructions = function() { return this.mInstructionList; };
BuildableSequence.prototype.getName = function() { return this.mName; };
BuildableSequence.prototype.listRobotClips = function() { return this.mClipList; };

/**
 * Adds the BuildableSequence to the provided application
 * @param {Application} pApp
 */
BuildableSequence.prototype.generate = function(pApp, pInitialSequence) {
  // Note that the initial sequence is added to the application by default. That being the case,
  // we do not need to create a new sequence here.
  const lIsMainSequence = this.getName() === pApp.getInitialSequence().getName();
  const lSeq = lIsMainSequence ? pInitialSequence : pApp.newSequence();
  if (!lIsMainSequence) {
    lSeq.setName(this.getName());
  }

  if (this.mIsLocked) {
    lSeq.setIsLocked(this.mIsLocked);
  }

  this.listInstructions().forEach(function(pInst) {
    pInst.generate(lSeq);
  });

  if (!this.robotParameters) {
    this.robotParameters = { clips: [] };
  }

  lSeq.robotParameters.clips = [];
  this.listRobotClips().forEach(function(pClip) {
    lSeq.robotParameters.clips.push(pClip.toJson());
  });
};

/**
 * Raw JSON twin of the BuildableSequence. ImmutableSequence cannot be changed
 * once it has been instantiated
 * @param {object} pSequenceJson Json representing a full sequence
 */
function ImmutableSequence(pSequenceJson) {
  this.mJson = pSequenceJson;
}

ImmutableSequence.prototype.getData = function() { return this.mJson; };

ImmutableSequence.prototype.generate = function(pApp) {
  pApp.addSequence(this.mJson);
};

ImmutableSequence.prototype.listInstructions = function() {
  return this.getData().instructions.map(instruction => new ImmutableInstruction(instruction));
};


function BuildableInstruction(pCategory, pName, pData) {
  this.mCategory  = pCategory;
  this.mName      = pName;
  this.mData      = pData;
}

BuildableInstruction.prototype.getCategory = function() { return this.mCategory; };
BuildableInstruction.prototype.getName = function() { return this.mName; };
BuildableInstruction.prototype.getData = function() { return this.mData; };
BuildableInstruction.prototype.generate = function(pSequence) {
  const lInst = pSequence.newInstruction(this.getCategory(), this.getName());
  if (!lInst) {
    console.error('Failed to generate instruction', this);
    return;
  }

  Object.keys(this.getData()).forEach((pKey) => {
    lInst.setParameter(pKey, this.getData()[pKey]);
  });
};

function ImmutableInstruction(pJson) {
  this.mJson = pJson;
}

ImmutableInstruction.prototype.getData = function() { return this.mJson.parameters; };

ImmutableInstruction.prototype.generate = function(pSequence) {
  pSequence.addInstruction(this.mJson);
};


function BuildableUi() {
  this.mTabs = [];
  this.mIsReadonly = false;
  this.mUseFullUiSpace = false;
}

BuildableUi.prototype.addTab = function(pTab) {
  this.mTabs.push(pTab);
  return this;
};

BuildableUi.prototype.generate = function() {
  const outUi = new ApplicationUI();
  outUi.mTabs = [];
  this.listTabs().forEach(function(pBuildableTab) {
    const lTabInfo = outUi.addTab();
    const lTab = lTabInfo.tab;

    lTab.setName(pBuildableTab.getName());

    pBuildableTab.listWidgets().forEach(function(pBuildableWidget) {
      const lWidget = lTab.addWidget(pBuildableWidget.getType());
      lWidget.mProps = pBuildableWidget.getData();
      lWidget.setPosition(pBuildableWidget.getPosition().x, pBuildableWidget.getPosition().y);
      lWidget.setDimension(pBuildableWidget.getDimension().w, pBuildableWidget.getDimension().h);
    });
  });
  outUi.setIsReadonly(this.mIsReadonly);
  outUi.setUseFullUiSpace(this.mUseFullUiSpace);
  return outUi;
};

BuildableUi.prototype.listTabs = function() { return this.mTabs; };

BuildableUi.prototype.setIsReadonly = function(pIsReadonly) {
  this.mIsReadonly = pIsReadonly;
};

BuildableUi.prototype.setUseFullUiSpace = function(pUseFullUiSpace) {
  this.mUseFullUiSpace = pUseFullUiSpace;
};

function BuildableUiTab(pName) {
  this.mName      = pName;
  this.mWidgets   = [];
}

BuildableUiTab.prototype.getName = function() { return this.mName; };

BuildableUiTab.prototype.addWidget = function() {
  for (let argIdx = 0; argIdx < arguments.length; argIdx++) {
    this.mWidgets.push(arguments[argIdx]);
  }
  return this;
};

BuildableUiTab.prototype.listWidgets = function() { return this.mWidgets; };

function BuildableWidget(pType, pData) {
  this.mType  = pType;
  this.mData  = pData;
  this.mPosition = { x: 0, y: 0 };
  this.mDimension = { w: 0, h: 0 };
}

BuildableWidget.prototype.getType = function() { return this.mType; };
BuildableWidget.prototype.getData = function() { return this.mData; };
BuildableWidget.prototype.position = function(pX, pY) {
  // Convert from grid coordinates to absolute cordinates
  const lX = (pX * uiWidgetDimensions.gridCellWidth) + (pX * uiWidgetDimensions.cellGutter);
  const lY = (pY * uiWidgetDimensions.gridCellHeight) + (pY * uiWidgetDimensions.cellGutter);
  this.mPosition = { x: lX, y: lY };
  return this;
};
BuildableWidget.prototype.dimension = function(pW, pH) {
  // Convert from grid coordinates to absolute coordinates
  const lW = (pW * uiWidgetDimensions.gridCellWidth) + ((pW - 1) * uiWidgetDimensions.cellGutter);
  const lH = (pH * uiWidgetDimensions.gridCellHeight) + ((pH - 1) * uiWidgetDimensions.cellGutter);
  this.mDimension = { w: lW, h: lH };
  return this;
};
BuildableWidget.prototype.getPosition = function() { return this.mPosition; };
BuildableWidget.prototype.getDimension = function() { return this.mDimension; };

module.exports = {
  MachineLogicAppBuilder  : MachineLogicAppBuilder,
  BuildableSequence       : BuildableSequence,
  ImmutableSequence       : ImmutableSequence,
  BuildableInstruction    : BuildableInstruction,
  ImmutableInstruction    : ImmutableInstruction,
  BuildableRobotClip      : BuildableRobotClip,
  ImmutableRobotClip      : ImmutableRobotClip,
  BuildableKeyframe       : BuildableKeyframe
};
